import { Component, OnDestroy, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiTextfield } from 'omnium-ui/form-field';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { ActivatedRoute } from '@angular/router';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../core/auth/action-authorization.service';
import {
  ProxeemaInvestorSignature,
  ProxeemaSignatureStateCategory,
  ProxeemaSignatureStateOption,
  ProxeemaSignatureDocument,
  ProxeemaUniversignMatch,
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';

type SignatureStateCard = {
  category: ProxeemaSignatureStateCategory;
  label: string;
  state: number | null;
  documentId: string | null;
  options: ProxeemaSignatureStateOption[];
};

@Component({
  selector: 'dbw-proxeema-investor-signatures-page',
  imports: [OuiButtonModule, OuiTextfield, StateCardComponent],
  templateUrl: './proxeema-investor-signatures.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './proxeema-investor-signatures.component.scss',
})
export class ProxeemaInvestorSignaturesPageComponent implements OnInit, OnDestroy {
  readonly investorIdInput = signal('');
  readonly reason = signal('');
  readonly signature = signal<ProxeemaInvestorSignature | undefined>(undefined);
  readonly documents = signal<ProxeemaSignatureDocument[]>([]);
  readonly stateOptions = signal<Record<ProxeemaSignatureStateCategory, ProxeemaSignatureStateOption[]>>({
    Der: [],
    Qpi: [],
    Recueil: [],
  });
  readonly loading = signal(true);
  readonly loadingInvestor = signal(false);
  readonly updatingCategory = signal<ProxeemaSignatureStateCategory | undefined>(undefined);
  readonly cancellingGedDocumentId = signal<string | undefined>(undefined);
  readonly loadingMatchesDocumentId = signal<string | undefined>(undefined);
  readonly confirmingDocumentId = signal<string | undefined>(undefined);
  readonly matches = signal<ProxeemaUniversignMatch[]>([]);
  readonly selectedDocument = signal<ProxeemaSignatureDocument | undefined>(undefined);
  readonly error = signal<string | undefined>(undefined);
  readonly canUpdateState = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureStateUpdate));
  readonly canCancelGedSignature = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureGedCancel));
  readonly canConfirmUniversignSignature = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.proxeemaSignatureUniversignConfirm));
  readonly cards = computed<SignatureStateCard[]>(() => {
    const signature = this.signature();
    const options = this.stateOptions();

    return [
      { category: 'Der', label: 'DER', state: signature?.derState ?? null, documentId: signature?.derDocumentId ?? null, options: options.Der },
      { category: 'Recueil', label: 'Recueil', state: signature?.recueilState ?? null, documentId: signature?.recueilDocumentId ?? null, options: options.Recueil },
      { category: 'Qpi', label: 'QPI', state: signature?.qpiState ?? null, documentId: signature?.qpiDocumentId ?? null, options: options.Qpi },
    ];
  });

  private stateOptionsSubscription?: Subscription;
  private investorSubscription?: Subscription;
  private updateSubscription?: Subscription;
  private routeSubscription?: Subscription;

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.stateOptionsSubscription = forkJoin({
      Der: this.apiService.getProxeemaSignatureStateOptions('Der'),
      Recueil: this.apiService.getProxeemaSignatureStateOptions('Recueil'),
      Qpi: this.apiService.getProxeemaSignatureStateOptions('Qpi'),
    })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: options => this.stateOptions.set(options),
        error: error => this.error.set(error?.error?.detail ?? 'Impossible de charger le catalogue des états.'),
      });

    // Permet d'arriver depuis le tableau des anomalies : /signatures-investisseur/:investorId
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      const investorId = params.get('investorId');
      if (!investorId || investorId === this.investorIdInput()) {
        return;
      }

      this.investorIdInput.set(investorId);
      this.loadInvestor();
    });
  }

  ngOnDestroy(): void {
    this.stateOptionsSubscription?.unsubscribe();
    this.investorSubscription?.unsubscribe();
    this.updateSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
  }

  onInvestorIdChange(value: string): void {
    this.investorIdInput.set(value);
  }

  onReasonChange(value: string): void {
    this.reason.set(value);
  }

  loadInvestor(): void {
    const investorId = this.getInvestorId();
    if (!investorId) {
      this.error.set("Saisissez un identifiant investisseur positif.");
      return;
    }

    this.loadingInvestor.set(true);
    this.error.set(undefined);
    this.investorSubscription?.unsubscribe();
    this.investorSubscription = this.apiService.getProxeemaInvestorSignature(investorId)
      .pipe(finalize(() => this.loadingInvestor.set(false)))
      .subscribe({
        next: signature => {
          this.signature.set(signature);
          this.loadDocuments(investorId);
        },
        error: error => {
          this.signature.set(undefined);
          this.documents.set([]);
          this.error.set(error?.status === 404
            ? 'Aucun avancement de signature n’a été trouvé pour cet investisseur.'
            : error?.error?.detail ?? "Impossible de charger les signatures de l'investisseur.");
        },
      });
  }

  updateState(card: SignatureStateCard, value: string): void {
    const investorId = this.getInvestorId();
    const newValue = Number(value);
    const reason = this.reason().trim();

    if (!investorId || card.state === null || !Number.isInteger(newValue) || !reason || reason.length > 500) {
      this.snackbarService.open('Un nouvel état et un motif de 500 caractères maximum sont requis.', 'error');
      return;
    }

    if (newValue === card.state || !window.confirm(`Modifier l’état ${card.label} de cet investisseur ?`)) {
      return;
    }

    this.updatingCategory.set(card.category);
    this.updateSubscription?.unsubscribe();
    this.updateSubscription = this.apiService.updateProxeemaSignatureState(investorId, card.category, {
      expectedValue: card.state,
      newValue,
      reason,
    })
      .pipe(finalize(() => this.updatingCategory.set(undefined)))
      .subscribe({
        next: signature => {
          this.signature.set(signature);
          this.reason.set('');
          this.snackbarService.open('État de signature mis à jour.', 'success');
        },
        error: error => this.snackbarService.open(
          error?.error?.detail ?? "La mise à jour de l'état de signature a échoué.",
          'error'),
      });
  }

  optionLabel(card: SignatureStateCard): string {
    return card.options.find(option => option.value === card.state)?.label ?? 'Non renseigné';
  }

  cancelGedSignature(document: ProxeemaSignatureDocument): void {
    const investorId = this.getInvestorId();
    const reason = this.reason().trim();
    if (!investorId || !reason || reason.length > 500) {
      this.snackbarService.open('Saisissez un motif de 500 caractères maximum avant l’annulation.', 'error');
      return;
    }
    if (!window.confirm(`Annuler définitivement la signature GED du document ${document.documentId} ?`)) return;

    this.cancellingGedDocumentId.set(document.documentId);
    this.apiService.cancelProxeemaGedSignature(investorId, document.documentId, { reason })
      .pipe(finalize(() => this.cancellingGedDocumentId.set(undefined)))
      .subscribe({
        next: result => {
          if (!result.cancelled) {
            this.snackbarService.open(result.error ?? 'L’annulation GED a échoué.', 'error');
            return;
          }
          this.reason.set('');
          this.snackbarService.open('Signature GED annulée.', 'success');
          this.loadDocuments(investorId);
        },
        error: error => this.snackbarService.open(error?.error?.detail ?? 'L’annulation GED a échoué.', 'error'),
      });
  }

  findUniversignMatches(document: ProxeemaSignatureDocument): void {
    const investorId = this.getInvestorId();
    if (!investorId) return;
    const until = new Date();
    const since = new Date(until);
    since.setDate(since.getDate() - 180);
    this.selectedDocument.set(document);
    this.matches.set([]);
    this.loadingMatchesDocumentId.set(document.documentId);
    this.apiService.getProxeemaUniversignMatches(investorId, document.documentId, since.toISOString(), until.toISOString())
      .pipe(finalize(() => this.loadingMatchesDocumentId.set(undefined)))
      .subscribe({
        next: matches => this.matches.set(matches),
        error: error => this.snackbarService.open(error?.error?.detail ?? 'La recherche Universign a échoué.', 'error'),
      });
  }

  confirmUniversignMatch(match: ProxeemaUniversignMatch): void {
    const investorId = this.getInvestorId();
    const document = this.selectedDocument();
    const reason = this.reason().trim();
    const category = this.categoryForRole(document?.role);
    const expectedState = category ? this.cards().find(card => card.category === category)?.state : null;
    if (!investorId || !document || !category || expectedState === null || !reason || reason.length > 500) {
      this.snackbarService.open('Sélectionnez un document suivi et saisissez un motif de 500 caractères maximum.', 'error');
      return;
    }
    const currentState: number = expectedState!;
    if (!window.confirm(`Confirmer la signature Universign ${match.transactionId} ?`)) return;
    this.confirmingDocumentId.set(document.documentId);
    this.apiService.confirmProxeemaUniversignSignature(investorId, document.documentId, {
      category, expectedState: currentState, universignTransactionId: match.transactionId, reason,
    }).pipe(finalize(() => this.confirmingDocumentId.set(undefined))).subscribe({
      next: result => {
        if (result.manualCorrectionRequired) {
          this.snackbarService.open(result.error ?? 'La GED a été mise à jour ; corrigez Minotaure manuellement.', 'warning');
        } else if (!result.confirmed) {
          this.snackbarService.open(result.error ?? 'La confirmation Universign a échoué.', 'error');
        } else {
          this.reason.set(''); this.snackbarService.open('Signature Universign confirmée.', 'success'); this.loadInvestor();
        }
      },
      error: error => this.snackbarService.open(error?.error?.detail ?? 'La confirmation Universign a échoué.', 'error'),
    });
  }

  private getInvestorId(): number | undefined {
    const value = Number(this.investorIdInput().trim());
    return Number.isInteger(value) && value > 0 ? value : undefined;
  }

  private loadDocuments(investorId: number): void {
    this.apiService.getProxeemaInvestorSignatureDocuments(investorId).subscribe({
      next: documents => this.documents.set(documents),
      error: () => this.documents.set([]),
    });
  }

  private categoryForRole(role?: string): ProxeemaSignatureStateCategory | undefined {
    return role === 'DER' ? 'Der' : role === 'Recueil' ? 'Recueil' : role === 'QPI' ? 'Qpi' : undefined;
  }
}